import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FieldError } from 'react-hook-form';

import { classList } from '@/helpers';
import { TOOLBAR_CONFIG } from './toolbarConfig';
import { media } from '@/styles/theme';
import { customChunkRenderer } from './customChunkRenderer';
import { customEntityTransform } from './customEntityTransform';

interface IWYSIWYGEditorProps {
  value: string | undefined;
  error?: FieldError;
  onChange: (value: string) => void;
}

const WYSIWYGEditor = React.forwardRef<Editor, IWYSIWYGEditorProps>(function WYSIWYGEditor(
  { value, onChange, ...rest }: IWYSIWYGEditorProps,
  ref,
): React.ReactElement {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [updated, setUpdated] = useState(false);

  const onEditorStateChange = (editorState: EditorState) => {
    setUpdated(true);
    setEditorState(editorState);
    const html = draftToHtml(
      convertToRaw(editorState.getCurrentContent()),
      undefined,
      undefined,
      customEntityTransform,
    );

    return onChange(html);
  };

  useEffect(() => {
    if (!updated) {
      const defaultValue = value ?? '';
      const blocksFromHtml = htmlToDraft(defaultValue, customChunkRenderer);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [updated, value]);

  return (
    <EditorContainer className={classList(rest.error && 'error')}>
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        toolbar={TOOLBAR_CONFIG}
        toolbarClassName="wysiwyg-toolbar"
        wrapperClassName="wysiwyg-wrapper"
        editorClassName="wysiwyg-editor"
        ref={ref}
      />
    </EditorContainer>
  );
});

export { WYSIWYGEditor };

const EditorContainer = styled.div`
  border: 1px solid #ced4da;
  border-radius: 8px;

  > * {
    color: #343a40;
  }

  &.error {
    border: 1px solid #dc3545 !important;
  }

  &:read-only {
    color: #868e96;
  }

  .wysiwyg-toolbar {
    border-radius: 8px;
    outline: 1px solid #ced4da;
    padding: 8px;
    row-gap: 8px;
    margin: 0;

    > * {
      margin: 0;
    }

    .rdw-option-wrapper {
      border: none;
      border-radius: 4px;
      box-shadow: none;
      height: 24px;
      width: 24px;
      min-width: 24px;
      padding: 4px;
      margin: 0 8px;

      &:hover {
        background-color: #e6f3fc;
      }
    }

    .rdw-option-active {
      border: 1px solid #0087dd;
      background-color: #e6f3fc;
    }

    .rdw-dropdown-wrapper {
      &:hover {
        background-color: #e6f3fc;
      }

      margin: 0 8px;
    }

    .rdw-dropdown-optionwrapper {
      width: 100%;
      border-radius: 0 0 4px 4px;

      &:hover {
        box-shadow: none;
      }
    }

    .rdw-dropdownoption-default {
      &:hover {
        background-color: #e6f3fc;
      }
    }

    .rdw-dropdown-selectedtext {
      font-size: 14px;
      font-weight: 400;
      color: #343a40;
      text-decoration: none;

      .rdw-dropdown-carettoopen,
      .rdw-dropdown-carettoclose {
        right: 8px;
      }
    }

    .rdw-fontfamily-dropdown {
      width: 100px;
      box-shadow: none;
      border-radius: 4px;
      border: 1px solid #ced4da;

      ${media.tablet} {
        width: 230px;
      }
    }

    .rdw-fontfamily-placeholder {
      max-width: initial;
    }

    .rdw-fontsize-dropdown {
      width: 50px;
      box-shadow: none;
      border-radius: 4px;
      border: 1px solid #ced4da;

      ${media.tablet} {
        width: 125px;
      }
    }

    .rdw-link-modal {
      height: initial;
      box-shadow: none;
      border-radius: 4px;
      border: 1px solid #ced4da;
      top: 38px;
      font-size: 14px;

      .rdw-link-modal-input {
        border-radius: 4px;
        border: 1px solid #ced4da;
      }

      .rdw-link-modal-btn {
        font-size: 14px;
      }

      .rdw-link-modal-btn {
        border-radius: 4px;

        &:hover {
          box-shadow: none;
          cursor: default;

          &:not([disabled]) {
            cursor: pointer;
            background-color: #e6f3fc;
            outline: 1px solid #0087dd;
          }
        }
      }
    }
  }

  .wysiwyg-editor {
    margin: 8px;
    min-height: 150px;
    overflow: auto;

    .rdw-link-decorator-icon {
      right: -16px;
      width: 16px;
      left: initial;
    }
  }
`;
